'use client'

import Link from 'next/link'
import { ArrowLeft } from 'lucide-react'

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-12 max-w-3xl">
        {/* Back link */}
        <Link 
          href="/" 
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-8"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to search
        </Link>

        {/* Header */}
        <h1 className="text-3xl font-bold text-foreground mb-2">Privacy Policy</h1>
        <p className="text-muted-foreground mb-8">Last updated: November 2025</p>

        {/* Content */}
        <div className="prose prose-stone dark:prose-invert max-w-none space-y-8">
          
          {/* Introduction */}
          <section>
            <p className="text-muted-foreground leading-relaxed">
              At Roostr, we take your privacy seriously. This policy explains what information 
              we collect, how we use it, and your rights regarding your personal data.
            </p>
          </section>

          {/* What We Collect */}
          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">1. What We Collect</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              We collect the following types of information:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-2">
              <li><strong>Account data:</strong> Email address and name (when you sign in with Google)</li>
              <li><strong>Search data:</strong> Your search queries, saved searches, and filter preferences</li>
              <li><strong>Usage data:</strong> Pages viewed, features used, and general interaction patterns</li>
              <li><strong>Device data:</strong> Browser type, device type, and approximate location (city-level)</li>
            </ul>
          </section>

          {/* How We Use Data */}
          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">2. How We Use Your Data</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              We use your information to:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-2">
              <li>Provide and maintain the search service</li>
              <li>Save your preferences and search history</li>
              <li>Improve our search algorithms and user experience</li>
              <li>Send notifications about your saved searches (if enabled)</li>
              <li>Analyze usage patterns to improve the platform (anonymized)</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-3">
              We do not sell your personal data to third parties.
            </p>
          </section>

          {/* Third-Party Services */}
          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">3. Third-Party Services</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              We use the following third-party services to operate Roostr:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-2">
              <li><strong>Google/Firebase:</strong> Authentication and user account management</li>
              <li><strong>Google Maps:</strong> Interactive map display for listing locations</li>
              <li><strong>OpenAI:</strong> Natural language search query processing</li>
              <li><strong>Elasticsearch:</strong> Search functionality and listing data storage</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-3">
              Each of these services has their own privacy policies governing their use of data.
            </p>
          </section>

          {/* Cookies */}
          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">4. Cookies</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              We use cookies for:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-2">
              <li><strong>Authentication:</strong> To keep you signed in to your account</li>
              <li><strong>Preferences:</strong> To remember your theme choice (light/dark mode)</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-3">
              We do not use advertising or tracking cookies.
            </p>
          </section>

          {/* Your Rights */}
          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">5. Your Rights (GDPR)</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              Under the General Data Protection Regulation (GDPR), you have the right to:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-2">
              <li><strong>Access:</strong> Request a copy of the personal data we hold about you</li>
              <li><strong>Rectification:</strong> Request correction of inaccurate data</li>
              <li><strong>Erasure:</strong> Request deletion of your personal data</li>
              <li><strong>Portability:</strong> Request your data in a machine-readable format</li>
              <li><strong>Withdraw consent:</strong> Opt out of data processing at any time</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-3">
              To exercise any of these rights, please contact us using the details below.
            </p>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">6. Data Retention</h2>
            <p className="text-muted-foreground leading-relaxed">
              We retain your account data for as long as your account is active. If you delete 
              your account, we will remove your personal data within 30 days. Anonymized usage 
              data may be retained indefinitely for analytical purposes.
            </p>
          </section>

          {/* Security */}
          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">7. Security</h2>
            <p className="text-muted-foreground leading-relaxed">
              We implement appropriate technical and organizational measures to protect your 
              personal data against unauthorized access, alteration, disclosure, or destruction. 
              However, no method of transmission over the internet is 100% secure.
            </p>
          </section>

          {/* Changes */}
          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">8. Changes to This Policy</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may update this privacy policy from time to time. We will notify you of any 
              significant changes by posting the new policy on this page and updating the 
              "Last updated" date.
            </p>
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-xl font-semibold text-foreground mb-3">9. Contact Us</h2>
            <p className="text-muted-foreground leading-relaxed">
              If you have any questions about this privacy policy or wish to exercise your 
              data rights, please contact us at{' '}
              <a href="mailto:privacy@roostr.me" className="text-primary hover:underline">
                privacy@roostr.me
              </a>
            </p>
          </section>

        </div>
      </div>
    </div>
  )
}

